import Emitter from '$lib/services/streaming/eventsDispatcher';
import Events from '$lib/services/streaming/eventsDispatcher/events';
import { StreamingStatsEvents } from '$lib/services/streaming/statistics';
import type { RTCPeerConnectionExtended } from '$lib/services/streaming/types';

import { StreamingType } from '@shared/types/room';

const peerConnectionConfig: RTCConfiguration = {
    iceServers: [],
    bundlePolicy: 'max-bundle',
};

const streamingType = StreamingType.Broadcast;

export const callWithTryCatch: CallableFunction = async (
    event: string,
    callback: () => Promise<unknown>,
    signalingState?: string
) => {
    let result = null;

    try {
        result = await callback();
    } catch (e: unknown) {
        const error = e as Error;
        const errorData = { errorText: error?.message, streamingType, signalingState };

        Emitter.emit(Events.STREAMING_STATS_WEBRTC_SEND_EVENT, event, errorData);
    }

    return result;
};

export default class WHIPClient {
    endpoint: string;
    localStream: MediaStream;
    peer: RTCPeerConnectionExtended;
    stopped = false;

    constructor(
        endpoint: string,
        userMedia: MediaStream,
        reduceBitrate: boolean,
        onError: (needRegen?: boolean) => void
    ) {
        this.endpoint = endpoint;
        this.localStream = userMedia;
        this.peer = new RTCPeerConnection(peerConnectionConfig) as RTCPeerConnectionExtended;
        this.peer.streamingType = StreamingType.Broadcast;
        this.peer.connectionId = window.crypto?.getRandomValues(new Uint32Array(1))?.[0] || 1;

        Emitter.emit(Events.STREAMING_STATS_SET_BROADCAST_START_TIMER);
        Emitter.emit(Events.STREAMING_STATS_WEBRTC_WATCH_PEER_CONNECTION, this.peer);
        this.setMediaStreamDirection(reduceBitrate);

        this.peer.addEventListener('negotiationneeded', async () => {
            await this.negotiateConnectionWithClientOffer(this.peer, this.endpoint, onError);
        });
    }

    private async negotiateConnectionWithClientOffer(
        pc: RTCPeerConnection,
        endpoint: string,
        onError: (needRegen?: boolean) => void,
        retry = 2
    ) {
        if (retry < 0) {
            console.error('Publish stream to cloudflare retry exceeded');
            onError(true);
            return;
        }

        const {
            createOffer,
            setLocalDescription,
            setRemoteDescription,
        } = pc;

        const {
            rtc_create_offer_error,
            rtc_set_local_description_error,
            rtc_ice_gathering_error,
            rtc_gather_candidates_error,
            rtc_post_sdp_offer_error,
            rtc_answer_sdp_error,
            rtc_set_remote_description_error,
            rtc_error,
        } = StreamingStatsEvents;

        const signalingState = pc?.signalingState;
        if (signalingState === 'closed') {
            !this.stopped && onError();
            return;
        }

        const createdOffer = await callWithTryCatch(rtc_create_offer_error, createOffer.bind(pc), signalingState);

        if (!createdOffer) {
            !this.stopped && onError();
            return;
        }

        const setLocalDescriptionResult = await callWithTryCatch(
            rtc_set_local_description_error,
            setLocalDescription.bind(pc, createdOffer),
            signalingState
        );

        if (setLocalDescriptionResult === null) {
            !this.stopped && onError();
            return;
        }

        const bindedGathering = this.waitToCompleteICEGathering.bind(pc, pc);
        const offer = await callWithTryCatch(
            rtc_ice_gathering_error,
            bindedGathering,
            signalingState
        ) as RTCSessionDescription;

        if (!offer) {
            const errorText = 'failed to gather ICE candidates for offer';
            const errorData = { streamingType, signalingState, errorText };

            Emitter.emit(Events.STREAMING_STATS_WEBRTC_SEND_EVENT, rtc_gather_candidates_error, errorData);
            !this.stopped && onError();
            return;
        }

        while (pc.connectionState !== 'closed') {
            const bindedSdpOffer = this.postSDPOffer.bind({}, endpoint, offer.sdp);
            const response = await callWithTryCatch(
                rtc_post_sdp_offer_error,
                bindedSdpOffer,
                signalingState
            ) as Response;

            if (response?.status === 201) {
                const answerSDP = await callWithTryCatch(
                    rtc_answer_sdp_error,
                    response.text.bind(response),
                    signalingState
                ) as string;

                if (pc.signalingState !== 'closed') {
                    const bindedSetRemoteSdp = setRemoteDescription.bind(pc, ({ type: 'answer', sdp: answerSDP }));

                    await callWithTryCatch(rtc_set_remote_description_error, bindedSetRemoteSdp, signalingState);
                } else {
                    if (this.stopped) {
                        return;
                    }
                    const errorText = 'Peer is closed';
                    const errorData = { streamingType, errorText, signalingState };

                    console.error(errorText);

                    Emitter.emit(Events.STREAMING_STATS_WEBRTC_SEND_EVENT, rtc_error, errorData);
                    !this.stopped && onError();
                    return;
                }

                return response.headers.get('Location');
            }

            let errorText = 'Unknown error: ' + response?.status;

            if (response?.status === 405) {
                errorText = 'Update the URL passed into the WHIP or WHEP client';
            } else if (response?.status === 400) {
                errorText = 'Couldn\'t publish stream to cloudflare, error 400';
            } else if (response) {
                errorText = await callWithTryCatch(response.text.bind(response)) as string;
            }

            const errorData = { streamingType, errorText, signalingState };

            Emitter.emit(Events.STREAMING_STATS_WEBRTC_SEND_EVENT, rtc_error, errorData);

            console.error(errorText);

            const reconnectionTimeout = 1000;
            if (!this.stopped) {
                await new Promise(() => setTimeout(
                    () => this.negotiateConnectionWithClientOffer(pc, endpoint, onError, retry - 1),
                    reconnectionTimeout
                ));
            }
        }
        if (pc.connectionState === 'closed') {
            !this.stopped && onError();
            return;
        }
    }

    private async postSDPOffer(endpoint: string, data: string) {
        return await fetch(endpoint, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'content-type': 'application/sdp',
            },
            body: data,
        });
    }

    private async waitToCompleteICEGathering(
        peerConnection: RTCPeerConnection
    ): Promise<RTCSessionDescription | null>  {
        return new Promise((resolve) => {
            setTimeout(function () {
                resolve(peerConnection.localDescription);
            }, 100);

            // eslint-disable-next-line
            peerConnection.onicegatheringstatechange = (ev) =>
                peerConnection.iceGatheringState === 'complete' &&
                resolve(peerConnection.localDescription);
        });
    }

    setMediaStreamDirection(reduceBitrate: boolean) {
        this.localStream
            .getTracks()
            .forEach((track) => {
                if (track.kind === 'video') {
                    this.peer.addTransceiver(track, {
                        sendEncodings: reduceBitrate ? [{
                            maxBitrate: 120_000,
                            scaleResolutionDownBy: 2,
                            maxFramerate: 15,
                        }] : [{
                            maxFramerate: 25,
                        }],
                        direction: 'sendonly',

                    });
                } else {
                    this.peer.addTransceiver(track, {
                        direction: 'sendonly',
                    });
                }
            });
    };

    stop() {
        this.stopped = true;
        this.peer.close();
    }
}
